import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue';
import QrcodeVue from 'qrcode.vue';
import router from '@/router';
import { isClerkAPIResponseError } from '@clerk/clerk-js';
import clerk, {
  getMessageKeyByClerkError,
  getClerkErrorCode,
} from '@/lib/clerk';
import { dispatchLogOut } from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import { store } from '@/store/index';
import { defineMessages } from '@/lib/i18n';
import { i18n as i18nModule } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import LogoutConfirmDialog from './LogoutConfirmDialog.vue';

type AddTotpSteps = 'add' | 'backupcodes';


export default /*#__PURE__*/_defineComponent({
  __name: 'TotpAuth',
  emits: ["sendCode"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'sendCode'): void;
}), expose: any, slots: any, attrs: any }) {

const $t = i18nModule.t.bind(i18nModule);

const m = defineMessages({
  addStepTitle: '1.QRコードの読み取り',
  addStepInstruction:
    'QRコードをスキャンするか、キーを認証アプリに入力して6桁のコードを取得してください',
  setupTitle: '2.認証コードの入力',
  setupInstructions: '認証アプリに表示される6桁のコードを入力してください',
  verifyCode: 'コードを確認',
  optionalRecoveryCodeSegment:
    'リカバリーコードの保存は任意ですが、強く推奨します。',
  backupCodesTitle: '3.リカバリーコードの保存 (任意）',
  looseBackupCodesSegment:
    '他に復旧手段がない場合、アカウントにアクセスできなくなる可能性があります。',
  backupCodesInstructions:
    'このコードは、スマートフォンの紛失や認証アプリが使えなくなった際にログインを復元するために利用できます。',
  looseBackupCodes: '保存しなくても認証は可能ですが、{0}',
  keepInSecurePlace:
    'パスワード管理ツールや紙など、安全な場所に保管することをおすすめします。',
  onlyOnce: '※この画面は一度しか表示されません。',
  finishSetup: '設定を完了する',
  recoveryCodeTitle: 'リカバリーコードを保存しましたか？',
  recoveryCodeMessage:
    'リカバリーコードを安全な場所に保存してください。この画面の後、再取得はできません。',
  recoveryCodeButton: 'コードを保存しました',
  secretKey: 'キー',
  print: '印刷',
});

const currentStep = ref<AddTotpSteps>('add');
const totpUri = ref<string | null>(null);
const totpSecret = ref<string | null>(null);
const totpCode = ref('');
const totpInput = ref(null);
const verificationPending = ref(false);
const errorAlert = ref('');
const showLogoutConfirmDialog = ref(false);
const showRecoveryCodeWarningDialog = ref(false);
const backupCodes = ref<string[]>([]);



const createTotp = async () => {
  try {
    const totp = await clerk.user.createTOTP();
    totpUri.value = totp.uri || null;
    totpSecret.value = totp.secret || null;
  } catch (error) {
    if (getClerkErrorCode(error) === 'totp_already_enabled') {
      currentStep.value = 'backupcodes';
      emits('sendCode');
      const codeRes = await clerk.user.createBackupCode();
      if (!codeRes.codes) {
        throw new Error('No backup codes returned from Clerk');
      }
      backupCodes.value = codeRes.codes;
    } else if (getClerkErrorCode(error) === 'session_reverification_required') {
      commitAddNotification(store, {
        content: getMessageKeyByClerkError(error),
        color: 'error',
      });
      router.push({ name: 'user' });
    } else if (isClerkAPIResponseError(error)) {
      // Handle other Clerk API errors
      errorAlert.value = getMessageKeyByClerkError(error);
    } else {
      console.error(error);
      errorAlert.value = getMessageKeyByClerkError(error);
    }
  }
};

const submitIfComplete = () => {
  if (!totpCode.value.match(/^[0-9]*$/)) {
    totpCode.value = totpCode.value.replace(/[^0-9]/g, '');
  }
  if (totpCode.value.length === 6 && !verificationPending.value) {
    verifyTotpCode();
  }
};

const handleKeydownOtp = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete'];
  const isDigit = /^\d$/.test(event.key);
  const isProcessKey = event.key === 'Process' || event.key === 'Unidentified';

  if ((!isDigit && !allowedKeys.includes(event.key)) || isProcessKey) {
    event.preventDefault();
  }
};

const copySecretKey = () => {
  if (totpSecret.value) {
    navigator.clipboard.writeText(totpSecret.value);
  }
};

const refreshBlocker = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = '';
};

const downloadBackupCodes = () => {
  const blob = new Blob([backupCodes.value.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'backup-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
};

const copyBackupCodes = () => {
  navigator.clipboard.writeText(backupCodes.value.join('\n'));
};

const printBackupCodes = () => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`<pre>${backupCodes.value.join('\n')}</pre>`);
    printWindow.print();
  }
};

const verifyTotpCode = async () => {
  errorAlert.value = '';
  verificationPending.value = true;

  try {
    const verifyRes = await clerk.user.verifyTOTP({ code: totpCode.value });

    if (!verifyRes) {
      throw new Error('No response returned from Clerk');
    }
    if (!verifyRes.backupCodes || verifyRes.backupCodes.length === 0) {
      // No backup codes were returned, this means that the codes have already been
      // generated at an earlier TFA setup and are still active. In this case, we
      // should explicitly generate new backup codes.
      const codeRes = await clerk.user.createBackupCode();
      if (!codeRes.codes) {
        throw new Error('No backup codes returned from Clerk');
      }
      backupCodes.value = codeRes.codes;
    } else {
      backupCodes.value = verifyRes.backupCodes;
    }
    currentStep.value = 'backupcodes';
    // Show a warning when refreshing the page after TOTP setup
    window.addEventListener('beforeunload', refreshBlocker);

    emits('sendCode');
  } catch (error) {
    if (getClerkErrorCode(error) === 'session_reverification_required') {
      commitAddNotification(store, {
        content: getMessageKeyByClerkError(error),
        color: 'error',
      });
      router.push({ name: 'user' });
      return;
    }
    console.error(error);
    totpInput.value?.focus();
    errorAlert.value =
      getMessageKeyByClerkError(error) || $t(sharedMessages.tfaInvalidCode);
  } finally {
    verificationPending.value = false;
  }
};

const completeTotpSetup = () => {
  showRecoveryCodeWarningDialog.value = true;
};

const handleRecoveryCodeAction = () => {
  console.log('Recovery code action');
  showRecoveryCodeWarningDialog.value = false;
  showLogoutConfirmDialog.value = true;
};

const handleLogoutConfirmation = (): void => {
  window.removeEventListener('beforeunload', refreshBlocker);
  dispatchLogOut(store, { tfaCompleted: true });
};

// Initialize TOTP creation when the component is mounted
createTotp();

return { __sfc: true,$t, m, currentStep, totpUri, totpSecret, totpCode, totpInput, verificationPending, errorAlert, showLogoutConfirmDialog, showRecoveryCodeWarningDialog, backupCodes, emits, createTotp, submitIfComplete, handleKeydownOtp, copySecretKey, refreshBlocker, downloadBackupCodes, copyBackupCodes, printBackupCodes, verifyTotpCode, completeTotpSetup, handleRecoveryCodeAction, handleLogoutConfirmation, QrcodeVue, sharedMessages, LogoutConfirmDialog }
}

})